import { faRobot } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import Slide from "@mui/material/Slide";
import { useEffect, useRef, useState } from "react";

import { getAIButtonAnimationCount } from "../../../api/quote/getAIButtonAnimationCount";
import { setAIButtonAnimationCount } from "../../../api/quote/setAIButtonAnimationCount";
import { TERTIARY_GREEN_COLOR } from "../../../theme";
import { CRMAppLocationIdentifier, FaqProps } from "../../../types/Faq";
import AIDisclaimerModal from "../../modals/AIDisclamerModal";
import InfoButton from "../InfoButton";
import APOutlineButton from "./APOutlineButton";

type AIButtonType = "vehicle-suggestions" | "vehicle-review";

const buttonLabelMap: Record<AIButtonType, string> = {
  "vehicle-suggestions": "AI Vehicle Suggestions",
  "vehicle-review": "AI Vehicle Review",
};

const AI_BUTTON_ANIMATION_THRESHOLD = 6;

const AIButton = ({
  type,
  onClick,
  shouldPromptForAIConsent,
  animateOnRender = false,
  faqProps,
  faqLocationIdentifier,
  textOverride,
}: {
  type: AIButtonType;
  onClick: () => void;
  shouldPromptForAIConsent: boolean;
  animateOnRender?: boolean;
  faqProps?: FaqProps;
  faqLocationIdentifier?: CRMAppLocationIdentifier;
  textOverride?: string;
}) => {
  const [showAIDisclaimer, setShowAIDisclaimer] = useState(false);
  const suggestionsContainerRef = useRef<HTMLElement>(null);

  const animationCount = getAIButtonAnimationCount();
  const { mutateAsync: mutateSetAIButtonAnimationCount } =
    setAIButtonAnimationCount();

  const shouldAnimate =
    animateOnRender &&
    animationCount.isSuccess &&
    (!animationCount.data ||
      animationCount.data < AI_BUTTON_ANIMATION_THRESHOLD);

  const endAdornment =
    faqProps && faqLocationIdentifier
      ? faqProps.renderAppLocation(
          faqLocationIdentifier,
          <InfoButton />,
          true,
          true
        )
      : undefined;

  useEffect(() => {
    if (shouldAnimate) {
      mutateSetAIButtonAnimationCount((animationCount.data ?? 0) + 1);
    }
  }, [shouldAnimate]);

  const onAIClick = () => {
    if (shouldPromptForAIConsent) {
      setShowAIDisclaimer(true);
    } else {
      onClick();
    }
  };

  return (
    <>
      <AIDisclaimerModal
        isOpen={showAIDisclaimer}
        onClose={() => setShowAIDisclaimer(false)}
        onConfirm={onClick}
        faqProps={faqProps}
        faqLocationIdentifier={faqLocationIdentifier}
      />
      <Box
        ref={suggestionsContainerRef}
        sx={{ overflow: "hidden", px: "8px", mx: "-8px" }}
      >
        <Slide
          direction="down"
          in={true}
          container={suggestionsContainerRef.current}
          timeout={1000}
          appear={shouldAnimate}
        >
          <Box>
            <APOutlineButton
              text={textOverride ?? buttonLabelMap[type]}
              color="tertiaryGreen"
              onClick={onAIClick}
              animateOnRender={shouldAnimate}
              startAdornment={
                <FontAwesomeIcon
                  icon={faRobot}
                  color={TERTIARY_GREEN_COLOR}
                  size="xl"
                />
              }
              endAdornment={endAdornment}
            />
          </Box>
        </Slide>
      </Box>
    </>
  );
};

export default AIButton;
